/**
 * Chart Colors Utility
 * Derives Dashboard chart palettes from the active tenant's brand colors
 * (theme.palette.primary / secondary), so charts follow per-tenant branding.
 *
 * Reuses the same lighten/darken helpers the theme generator uses, keeping
 * chart colors cohesive with the rest of the branded UI.
 */

import { lightenColor, darkenColor } from './themeGenerator';

/**
 * Ordered categorical palette derived from brand colors.
 * Alternates primary/secondary and their tints so adjacent slices stay distinct.
 * Pie charts index into this with `index % length`, so any slice count wraps.
 * @param {string} primary - Brand primary color (hex)
 * @param {string} secondary - Brand secondary color (hex)
 * @returns {string[]} Ordered list of chart colors
 */
export const getChartPalette = (primary, secondary) => [
  primary,
  secondary,
  lightenColor(primary, 30),
  lightenColor(secondary, 30),
  darkenColor(primary, 20),
  darkenColor(secondary, 20),
  lightenColor(primary, 55),
  lightenColor(secondary, 55),
];

/**
 * Branded colors for the two-segment Program Capacity bar.
 * Used = brand primary, Headroom = a muted secondary tint.
 * @param {string} primary - Brand primary color (hex)
 * @param {string} secondary - Brand secondary color (hex)
 * @returns {{used: string, headroom: string}}
 */
export const getCapacityColors = (primary, secondary) => ({
  used: primary,
  headroom: lightenColor(secondary, 25),
});

/**
 * Single-accent + muted "Other" colors for the Top Investors bar chart.
 * @param {string} primary - Brand primary color (hex)
 * @returns {{bar: string, other: string}}
 */
export const getInvestorColors = (primary) => ({
  bar: primary,
  other: lightenColor(primary, 60),
});
